import * as React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import ImageWithBadge from '@components/ImageWithBadge';
import CounterBox, {
  CounterBoxProps,
} from '@components/TableComponents/CounterBox';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { useKeycloakContext } from '@common/context/keycloakContext';
import DashButton, { DashButtonProps } from '@components/DashButton';

const Styled = {
  FlexRow: styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    rowGap: '20px',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
    },
  })),
  LogoNameBox: styled(Box)({
    display: 'flex',
    alignItems: 'center',
    columnGap: '20px',
    minWidth: '0px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    img: {
      objectFit: 'contain',
      padding: '8px',
    },
  }),
  CounterDiv: styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down('md')]: {
      gap: '8px',
    },
  })),
  PageHeaderTitle: styled(Typography)(({ theme }) => ({
    marginBottom: '0.3em',
    fontSize: '2.2rem',
    color: theme.palette.GOLD_1.main,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  })),
};

export interface SectionHeaderProps {
  sectionTitle: string;
  logo?: string;
  subtitleButton?: DashButtonProps;
  counterProps?: CounterBoxProps[];
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  sectionTitle,
  logo,
  subtitleButton,
  counterProps,
}) => {
  const featureFlags = useFeatureFlags();
  const theme = useTheme();
  const { isEcAdmin, isEmployer } = useKeycloakContext();
  const isEcAdminOrEmployer = isEcAdmin || isEmployer;

  return (
    <>
      <Styled.FlexRow
        sx={{
          ...(counterProps && {
            mb: '48px',
            [theme.breakpoints.down('sm')]: {
              mb: '28px',
            },
          }),
        }}
      >
        <Styled.LogoNameBox>
          {logo && <ImageWithBadge src={logo} height="100px" />}
          <Styled.PageHeaderTitle
            variant="EC_TYPE_3XL"
            data-testid="dashboard-page-title"
          >
            {sectionTitle}
          </Styled.PageHeaderTitle>
        </Styled.LogoNameBox>
        {counterProps && featureFlags.EMPLOYER_MARKETING_METRICS && (
          <Styled.CounterDiv>
            {counterProps.map((counter) => (
              <CounterBox
                key={counter.title}
                title={counter.title}
                count={counter.count}
                resizeBreakpoint={900}
              />
            ))}
          </Styled.CounterDiv>
        )}
      </Styled.FlexRow>
      {subtitleButton && isEcAdminOrEmployer && (
        <DashButton
          href={subtitleButton.href}
          variant={subtitleButton.variant}
          icon={subtitleButton.icon}
          dataTestId={subtitleButton.dataTestId}
          label={subtitleButton.label}
          sx={{
            minWidth: 0,
            minHeight: 0,
            height: 'unset',
            width: 'unset',
            padding: 0,
            color: theme.palette.GRAY_3.main,
          }}
        />
      )}
    </>
  );
};

export default SectionHeader;
